import type { HullDimensions } from '../hullSize.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { courseModel, tailAt, type Course } from './course.ts';
import { fastStep, hullObstacle, stepContext, type FleetHull } from './fastStep.ts';
import type { SearchKnobs } from './levels.ts';
import type { RivalSeat } from './pilot.ts';
import { DEAD_END_TURNS, planRoots } from './planner.ts';

/** Where a rival is expected to be after each of its next moves, by its own best line. */
export type RivalForecast = {
  seat: RivalSeat;
  /** Pose after its 1st, 2nd, … move. Empty for a finished boat: it stays where it is. */
  poses: BoatState[];
};

function searchInput(course: Course, rival: RivalSeat, knobs: SearchKnobs, discs: FleetHull[]) {
  return {
    ctx: stepContext(course.map, rival.feel, course.raster, rival.dimensions),
    model: courseModel(course, rival.feel),
    knobs: { ...knobs, verify: 0 },
    accelPx: rival.feel.reach?.accel ?? rival.feel.radius,
    brakePx: rival.feel.reach?.brake ?? rival.feel.radius,
    discs,
  };
}

/** The other rivals, parked where they are, as obstacles for `rival`. */
function othersAround(rival: RivalSeat, rivals: readonly RivalSeat[]): FleetHull[] {
  return rivals
    .filter((other) => other.id !== rival.id)
    .map((other) => hullObstacle(other.boat, other.dimensions));
}

/**
 * Each rival's own best line from where it is, with the other rivals parked. This bot is left
 * out: it moves first, and guessing how the rival reacts to that is one level deeper than this
 * goes. A rival's line is replayed through the fast model to get its poses.
 */
export function forecastRivals(
  course: Course,
  rivals: readonly RivalSeat[],
  knobs: SearchKnobs,
): RivalForecast[] {
  return rivals.map((seat) => {
    if (seat.finished || seat.race.complete) {
      return { seat, poses: [] };
    }

    const input = searchInput(course, seat, knobs, othersAround(seat, rivals));
    const best = planRoots(seat.boat, seat.race, input)[0];
    const poses: BoatState[] = [];
    let boat = seat.boat;
    let race = seat.race;

    for (const dest of best?.line ?? []) {
      const step = fastStep(input.ctx, boat, race, dest);

      if (!step) {
        break;
      }

      boat = step.boat;
      race = step.race;
      poses.push(boat);

      if (race.complete) {
        break;
      }
    }

    return { seat, poses };
  });
}

/**
 * Rival hulls for this bot's moves 2, 3, …: during its move d every rival has made d − 1 moves of
 * its forecast (Sequential), or sits still once the forecast runs out or the boat has finished.
 * On move 2 each rival is also where it would be if it braked as hard as it can, straight on:
 * a forecast is a guess, and a boat that follows closer than the one ahead can brake has nowhere
 * to go when it does.
 */
export function predictedHulls(
  forecasts: readonly RivalForecast[],
  plies: number,
  use: { forecast: boolean; brake: boolean } = { forecast: true, brake: true },
): FleetHull[][] {
  const out: FleetHull[][] = [];

  for (let moves = 1; moves < plies; moves += 1) {
    const hulls: FleetHull[] = [];

    for (const { seat, poses } of forecasts) {
      if (use.forecast) {
        const pose = poses[Math.min(moves, poses.length) - 1] ?? seat.boat;
        hulls.push(hullObstacle(pose, seat.dimensions));
      }

      if (use.brake && moves === 1 && poses.length > 0) {
        hulls.push(hullObstacle(fullBrake(seat), seat.dimensions));
      }
    }

    out.push(hulls);
  }

  return out;
}

/** Where a rival ends its next move if it sheds all the speed its hull allows, straight on. */
function fullBrake(seat: RivalSeat): BoatState {
  const brake = seat.feel.reach?.brake ?? seat.feel.radius;
  const speed = Math.max(0, seat.boat.speed - brake);

  return {
    position: {
      x: seat.boat.position.x + Math.cos(seat.boat.heading) * speed,
      y: seat.boat.position.y + Math.sin(seat.boat.heading) * speed,
    },
    heading: seat.boat.heading,
    speed,
    drive: seat.boat.drive,
  };
}

/** Turns a rival is still expected to need: its best light plan with `blocker` parked in the way. */
export function rivalTurnsWith(
  course: Course,
  rival: RivalSeat,
  rivals: readonly RivalSeat[],
  knobs: SearchKnobs,
  blocker: { boat: BoatState; dimensions: HullDimensions } | null,
): number {
  const discs = othersAround(rival, rivals);

  if (blocker) {
    discs.push(hullObstacle(blocker.boat, blocker.dimensions));
  }

  const input = searchInput(course, rival, knobs, discs);
  const best = planRoots(rival.boat, rival.race, input)[0];

  if (!best) {
    // Boxed in on every click: what it had left, plus a dead end.
    return tailAt(input.model, rival.race, rival.boat.position).time + DEAD_END_TURNS;
  }

  return best.score + (best.finished || best.depth >= knobs.depth ? 0 : DEAD_END_TURNS);
}

/**
 * Rivals a block is worth weighing against: close in the race (within `turnsApart` of this
 * boat's turns to go) and close on the water.
 */
export function rivalsInReach(
  course: Course,
  rivals: readonly RivalSeat[],
  myTurnsToGo: number,
  myPosition: Vec2,
  reachPx: number,
  turnsApart: number,
): RivalSeat[] {
  return rivals.filter((rival) => {
    if (rival.finished || rival.race.complete) {
      return false;
    }

    const gap = Math.hypot(rival.boat.position.x - myPosition.x, rival.boat.position.y - myPosition.y);

    if (gap > reachPx) {
      return false;
    }

    const theirs = tailAt(courseModel(course, rival.feel), rival.race, rival.boat.position).time;
    return Math.abs(theirs - myTurnsToGo) <= turnsApart;
  });
}
